/**
 * @file shop-font-errors.ts
 * @description Ошибки сервера по своим шрифтам магазина (phase19.16 Р1-7;
 * SB99): коды `admin/site-view/fonts` → фразы для человека, разбор 409
 * FONT_IN_USE («где используется»), повтор имени и сбой связи.
 *
 * Реестр «код → фраза» (OCP): новый код — новая строка. Сервер шлёт коды в
 * нижнем регистре (`font_limit`), сравниваем без учёта регистра.
 */

import { ApiError } from "../api-client";
import { FONT_FILE_TEXT, type ShopFontUsage } from "./shop-fonts";

/** Код ошибки в верхнем регистре. */
export function fontErrorCode(error: unknown): string | null {
  // Ответ без `error.code` (500 с `{ error: { message } }`) — кода нет, а не исключение.
  return error instanceof ApiError && typeof error.code === "string" ? error.code.toUpperCase() : null;
}

/** Список своих шрифтов не загрузился — общий текст и при обрыве связи. */
export const FONT_LIST_ERROR_TEXT = "Не удалось загрузить список ваших шрифтов";
export const FONT_LIST_NETWORK_TEXT = "Не удалось загрузить список ваших шрифтов — нет связи с сервером.";

/** Текст ошибки списка: любой сбой завершает загрузку этой фразой. */
export function fontListErrorText(error: unknown): string {
  return isFontNetworkError(error) ? FONT_LIST_NETWORK_TEXT : FONT_LIST_ERROR_TEXT;
}

export const FONT_NETWORK_TEXT =
  "Не удалось загрузить шрифт — нет связи с сервером. Файлы остались в окне, попробуйте ещё раз.";

export const INSPECT_NETWORK_TEXT = "Не удалось проверить файл — нет связи с сервером. Выберите его ещё раз.";

const FONT_ERROR_TEXT: Readonly<Record<string, string>> = {
  FONT_FILE_INVALID: FONT_FILE_TEXT.invalid,
  FONT_FILE_FORMAT: FONT_FILE_TEXT.format,
  FONT_FILE_TOO_LARGE: FONT_FILE_TEXT.size,
  FONT_LIMIT: "Загружено 5 из 5 шрифтов. Чтобы добавить новый, удалите тот, что не используется.",
  FONT_RIGHTS_REQUIRED: "Отметьте, что у вас есть право использовать этот шрифт на сайте.",
  FONT_FILE_EXISTS: "Это начертание уже загружено. Уберите файл из окна или положите его в свободное место.",
  FONT_IN_USE: "Шрифт используется на сайте или в дизайне — удалить его нельзя.",
  FONT_NOT_FOUND: "Шрифт уже удалили — выберите другой.",
  FONT_FILE_REQUIRED: "Добавьте файл обычного начертания — без него шрифт не загрузить.",
  FONT_FAMILY_INVALID: "Название шрифта — от 1 до 60 знаков.",
  FONT_CATEGORY_INVALID: "Выберите вид шрифта.",
  FONT_SOURCE_NOTE_TOO_LONG: "Поле «Откуда шрифт» — не длиннее 300 знаков.",
  FONT_FACE_INVALID: "Шрифт не подходит для сайта — возьмите другой файл.",
  NETWORK_ERROR: FONT_NETWORK_TEXT,
};

/** «Шрифт «Marianna» у вас уже есть…» — повтор имени при загрузке. */
export function familyTakenText(family: string): string {
  return `Шрифт «${family}» у вас уже есть. Чтобы добавить начертание, нажмите ⋯ у шрифта → «Добавить начертание».`;
}

export function fontErrorText(error: unknown, fallback: string): string {
  const code = fontErrorCode(error);
  if (code === null) return fallback;
  return FONT_ERROR_TEXT[code] ?? ((error as ApiError).message || fallback);
}

export function isFontNetworkError(error: unknown): boolean {
  return fontErrorCode(error) === "NETWORK_ERROR";
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

/** 409 FONT_IN_USE → где используется (details сервера); `null` — другая ошибка. */
export function parseFontInUse(error: unknown): ShopFontUsage | null {
  if (fontErrorCode(error) !== "FONT_IN_USE") return null;
  const raw = (error as ApiError).details;
  const d = isObject(raw) ? raw : {};
  const designs = Array.isArray(d.designs)
    ? d.designs.filter(isObject).map((x) => ({ id: String(x.id ?? ""), name: String(x.name ?? "") }))
    : [];
  // Сервер: `{ published: роли, designs }`; `publishedRoles` — на случай смены имени.
  return { draftRoles: [], publishedRoles: strings(d.published ?? d.publishedRoles), designs };
}
